import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import moment from "moment";
import dayjs from "dayjs";
import { doc, setDoc } from "firebase/firestore";

import useAdminDataStore, {
  selectCurrentGroupId,
  selectAllGroups,
} from "../../../../../../store/adminData/adminData";
import useUserStore, {
  selectUserDetails,
} from "../../../../../../store/user/user";
import {
  fbGetFormLayout,
  fbSaveFormLayout,
  publishOrUnpublishForm,
  uploadImage,
} from "../../../../../../services/firebaseService/endPoints/admin/members";
import { fbUpdateGroup } from "../../../../../../services/firebaseService/endPoints/group";
import { fbGetGroup } from "../../../../../../services/firebaseService/endPoints/user";
import { fbGetPartners } from "../../../../../../services/firebaseService/endPoints/admin/partners";
import {
  generateQR,
  postUpdateCDNUtil,
} from "../../../../../../helperFunctions/util";
import { getWebsiteConfig } from "../../../../../../services/firebaseService/endPoints/admin/website";
import { db } from "../../../../../../services/firebaseService/connection";
import RegFormDetails from "../../../../../../models/admin/RegFormDetails";
import logger from "../../../../../../utils/logger";

const useRegistrationForm = () => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];
  const user = useUserStore(selectUserDetails); // eslint-disable-line no-unused-vars
  const groupName = adminData?.data?.groupName;

  const requireMembershipPackageRef = useRef(null);

  // Form state
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [benefits, setBenefits] = useState(EditorState.createEmpty());
  const [groupImageFile, setGroupImageFile] = useState(undefined);
  const [appScreenshotFile, setAppScreenshotFile] = useState(undefined);
  const [groupImageWideList, setGroupImageWideList] = useState([]);
  const [appScreenshotList, setAppScreenshotList] = useState([]);
  const [groupImageRegularList, setGroupImageRegularList] = useState([]); // eslint-disable-line no-unused-vars
  const [groupImageType, setGroupImageType] = useState("wide"); // eslint-disable-line no-unused-vars
  const [groupTermsURL, setGroupTermsURL] = useState("");
  const [thankYouNote, setThankYouNote] = useState("");
  const [renewalDate, setRenewalDate] = useState(""); // eslint-disable-line no-unused-vars
  const [supportEmail, setSupportEmail] = useState(""); // eslint-disable-line no-unused-vars
  const [processingFee, setProcessingFee] = useState("");
  const [requireMembershipPackage, setRequireMembershipPackage] =
    useState(false);

  // Step state
  const [basicsStep, setBasicsStep] = useState("process");
  const [formFieldStep, setFormFieldStep] = useState("wait");
  const [donationStep, setDonationStep] = useState("wait");
  const [previewStep, setPreviewStep] = useState("wait");
  const [stripeSetupConfirmation, setStripeSetupConfirmation] = useState(false);

  // Form fields
  const [formFields, setFormFields] = useState({
    fullName: true,
    firstName: true,
    lastName: true,
    email: true,
    phone: "",
    address: "",
    birthday: "",
    joinMailingList: "",
    shareVolunteerOpportunities: "",
    officialClubMember: "",
    seasonTicketHolder: "",
  });
  const [customFieldDetails, setCustomFieldDetails] = useState({});

  // Chapters
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [includeNone, setIncludeNone] = useState(false);

  // Packages
  const [packages, setPackages] = useState([]);

  // Donations
  const [hasDonations, setHasDonations] = useState(false);
  const [donationTitle, setDonationTitle] = useState("");
  const [donationDesc, setDonationDesc] = useState("");
  const [donationImageList, setDonationImageList] = useState([]);
  const [donationImage, setDonationImage] = useState("");
  const [amount1, setAmount1] = useState("");
  const [amount2, setAmount2] = useState("");
  const [amount3, setAmount3] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [donationAutorenewal, setDonationAutorenewal] = useState(false);
  const [donationStripeProductPartnerId, setDonationStripeProductPartnerId] =
    useState("");

  // UI state
  const [isPublished, setIsPublished] = useState(false);
  const [editConfiguration, setEditConfiguration] = useState({
    groupImageChanged: false,
    donationImageChanged: false,
    appScreenshotChanged: false,
  });
  const [errorInForm, setErrorInForm] = useState({
    visible: false,
    isError: false,
    message: "",
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previewVisible, setPreviewVisible] = useState("false"); // eslint-disable-line no-unused-vars
  const [isPublishing, setIsPublishing] = useState(false);
  const [addOrEditPackageModal, setAddOrEditPackageModal] = useState(false); // eslint-disable-line no-unused-vars
  const [allPartners, setAllPartners] = useState([]);
  const [joinSVG, setJoinSVG] = useState("");
  const [addressVerification, setAddressVerification] = useState(null);
  const [addressVerificationTriggered, setAddressVerificationTriggered] =
    useState(false);
  const [highlightPackageRequirement, setHighlightPackageRequirement] =
    useState(false);

  // --- Helpers ---

  const setStepState = (stepName, value) => {
    switch (stepName) {
      case "basicsStep":
        setBasicsStep(value);
        break;
      case "formFieldStep":
        setFormFieldStep(value);
        break;
      case "donationStep":
        setDonationStep(value);
        break;
      case "previewStep":
        setPreviewStep(value);
        break;
      default:
        break;
    }
  };

  const applyRegFormData = (data) => {
    if (data.title !== undefined) setTitle(data.title);
    if (data.intro !== undefined) setIntro(data.intro);
    if (data.benefits !== undefined) setBenefits(data.benefits);
    if (data.groupImageFile !== undefined) setGroupImageFile(data.groupImageFile);
    if (data.appScreenshotFile !== undefined)
      setAppScreenshotFile(data.appScreenshotFile);
    if (data.groupImageWideList !== undefined)
      setGroupImageWideList(data.groupImageWideList);
    if (data.groupImageRegularList !== undefined)
      setGroupImageRegularList(data.groupImageRegularList);
    if (data.groupImageType !== undefined) setGroupImageType(data.groupImageType);
    if (data.groupTermsURL !== undefined) setGroupTermsURL(data.groupTermsURL);
    if (data.thankYouNote !== undefined) setThankYouNote(data.thankYouNote);
    if (data.renewalDate !== undefined) setRenewalDate(data.renewalDate);
    if (data.supportEmail !== undefined) setSupportEmail(data.supportEmail);
    if (data.processingFee !== undefined) setProcessingFee(data.processingFee);
    if (data.requireMembershipPackage !== undefined)
      setRequireMembershipPackage(data.requireMembershipPackage);
    if (data.stripeSetupConfirmation !== undefined)
      setStripeSetupConfirmation(data.stripeSetupConfirmation);
    if (data.formFields !== undefined) setFormFields(data.formFields);
    if (data.customFieldDetails !== undefined)
      setCustomFieldDetails(data.customFieldDetails);
    if (data.chapters !== undefined) setChapters(data.chapters);
    if (data.includeNone !== undefined) setIncludeNone(data.includeNone);
    if (data.packages !== undefined) setPackages(data.packages);
    if (data.hasDonations !== undefined) setHasDonations(data.hasDonations);
    if (data.donationTitle !== undefined) setDonationTitle(data.donationTitle);
    if (data.donationDesc !== undefined) setDonationDesc(data.donationDesc);
    if (data.donationImageList !== undefined)
      setDonationImageList(data.donationImageList);
    if (data.donationImage !== undefined) setDonationImage(data.donationImage);
    if (data.amount1 !== undefined) setAmount1(data.amount1);
    if (data.amount2 !== undefined) setAmount2(data.amount2);
    if (data.amount3 !== undefined) setAmount3(data.amount3);
    if (data.partnerId !== undefined) setPartnerId(data.partnerId);
    if (data.isPublished !== undefined) setIsPublished(data.isPublished);
    if (data.donationAutorenewal !== undefined)
      setDonationAutorenewal(data.donationAutorenewal);
    if (data.donationStripeProductPartnerId !== undefined)
      setDonationStripeProductPartnerId(data.donationStripeProductPartnerId);
    if (data.editConfiguration !== undefined)
      setEditConfiguration(data.editConfiguration);
    if (data.previewVisible !== undefined) setPreviewVisible(data.previewVisible);
    if (data.joinSVG !== undefined) setJoinSVG(data.joinSVG);
    if (data.addressVerification !== undefined)
      setAddressVerification(data.addressVerification);
  };

  // --- Handlers ---

  const handleEditorStateChange = (editorState) => {
    setBenefits(editorState);
  };

  const handleInputChange = (e) => {
    switch (e.target.name) {
      case "title":
        setTitle(e.target.value);
        break;
      case "intro":
        setIntro(e.target.value);
        break;
      case "groupTermsURL":
        setGroupTermsURL(e.target.value);
        break;
      case "thankYouNote":
        setThankYouNote(e.target.value);
        break;
      case "supportEmail":
        setSupportEmail(e.target.value);
        break;
      case "renewalDate":
        setRenewalDate(e.target.value);
        break;
      default:
        break;
    }
  };

  const changeSelect = (name, value) => {
    switch (name) {
      case "processingFee":
        setProcessingFee(value);
        break;
      default:
        break;
    }
  };

  const uploadAction = (file, name) => {
    if (name === "groupImageFile") setGroupImageFile(file);
    else if (name === "appScreenshotFile") setAppScreenshotFile(file);
  };

  const handleStripeSetupConfirmation = (value) => {
    setStripeSetupConfirmation(value);
  };

  const handleImageChange = (fileList, fileNameKey, fileListKey, type) => {
    let result = {};
    if (fileList.length === 0) {
      result[fileNameKey] = null;
    } else {
      result[fileNameKey] = fileList[0].originFileObj;
    }

    result[fileListKey] = fileList;
    if (basicsStep === "process") {
      result.groupImageType = type;
    }
    result.editConfiguration =
      basicsStep === "process"
        ? {
            groupImageChanged: true,
            appScreenshotChanged: true,
            donationImageChanged: false,
          }
        : {
            groupImageChanged: false,
            appScreenshotChanged: false,
            donationImageChanged: true,
          };

    if (fileNameKey === "groupImageFile")
      setGroupImageFile(result[fileNameKey]);
    else if (fileNameKey === "appScreenshotFile")
      setAppScreenshotFile(result[fileNameKey]);
    else if (fileNameKey === "donationImage")
      setDonationImage(result[fileNameKey]);

    if (fileListKey === "groupImageWideList")
      setGroupImageWideList(result[fileListKey]);
    else if (fileListKey === "appScreenshotList")
      setAppScreenshotList(result[fileListKey]);
    else if (fileListKey === "donationImageList")
      setDonationImageList(result[fileListKey]);

    if (basicsStep === "process" && result.groupImageType)
      setGroupImageType(result.groupImageType);
    setEditConfiguration(result.editConfiguration);
  };

  const handleDateChange = (name, value) => {
    if (!Array.isArray(value)) value = moment(value).isValid() ? value : "";
    let hours = 0,
      minutes = 0,
      seconds = 59;
    if (value) {
      const computed = moment(value)
        .set({
          h: hours,
          m: minutes,
          second: seconds,
        })
        .unix();
      if (name === "renewalDate") setRenewalDate(computed);
    }
  };

  const handleCheckChange = (e) => {
    setFormFields((prevFormFields) => ({
      ...prevFormFields,
      [e.target.id]: e.target.checked,
    }));
  };

  const handleErrorMessageClose = () => {
    setErrorInForm((prevErrorInForm) => ({
      ...prevErrorInForm,
      visible: false,
    }));
  };

  const handleDonationAutoRenewalSubmit = (paymentDetails = {}) => {
    if (!donationAutorenewal || !partnerId) return;
    if (donationStripeProductPartnerId === partnerId) return;

    const reqBody = {
      partnerId: partnerId,
      merchantId: paymentDetails.stripeAccountId || "",
      name: donationTitle,
      groupId: currentGroup,
    };

    const url = `${import.meta.env.VITE_GCP_BASE_URL}/stripe2/createDonationProduct`;

    axios
      .post(url, JSON.stringify(reqBody), {
        headers: { "content-type": "text/json" },
      })
      .then(() => {
        const newDonationStripeProductPartnerId = partnerId;
        setDonationStripeProductPartnerId(newDonationStripeProductPartnerId);
        setDoc(
          doc(db, "registration_form_layout", currentGroup),
          {
            donationStripeProductPartnerId: newDonationStripeProductPartnerId,
          },
          { merge: true },
        );
      })
      .catch((err) => {
        logger.error("Error creating donation Stripe product:", err);
        toast.error("Failed to setup partner auto-renewal");
      });
  };

  const collectFormState = () => ({
    loading,
    title,
    intro,
    benefits: draftToHtml(convertToRaw(benefits.getCurrentContent())),
    groupImageFile,
    appScreenshotFile,
    groupImageWideList,
    appScreenshotList,
    groupImageRegularList,
    groupImageType,
    groupTermsURL,
    thankYouNote,
    renewalDate,
    supportEmail,
    processingFee,
    requireMembershipPackage,
    basicsStep,
    formFieldStep,
    donationStep,
    stripeSetupConfirmation,
    previewStep,
    formFields,
    customFieldDetails,
    showChapterModal,
    chapters,
    includeNone,
    packages,
    hasDonations,
    donationTitle,
    donationDesc,
    donationImageList,
    donationImage,
    amount1,
    amount2,
    amount3,
    partnerId,
    isPublished,
    donationAutorenewal,
    donationStripeProductPartnerId,
    editConfiguration,
    errorInForm,
    isTransitioning,
    previewVisible,
    isPublishing,
    addOrEditPackageModal,
    allPartners,
    joinSVG,
    addressVerification,
    addressVerificationTriggered,
    highlightPackageRequirement,
  });

  const handleNext = (current, next) => {
    setLoading(true);
    setIsTransitioning(true);
    let currentPackages = packages;
    let promise;
    if (
      current === "basicsStep" &&
      editConfiguration.groupImageChanged &&
      groupImageFile
    ) {
      let groupImageUploadPromise = uploadImage(
        groupImageFile,
        `/regForm/${currentGroup}/groupImage`,
      ).then(({ downloadUrl }) => downloadUrl);
      promise = groupImageUploadPromise;
    }

    if (
      current === "basicsStep" &&
      editConfiguration.appScreenshotChanged &&
      appScreenshotFile
    ) {
      const storageDirectory = `${groupName}/website/appScreenshots`;
      const imagePath = `${storageDirectory}/${currentGroup}-app-screenshot.png`;

      uploadImage(appScreenshotFile, imagePath).then(({ downloadUrl }) => {
        let docRef = doc(db, "website", currentGroup);
        setDoc(docRef, { appScreenshot: downloadUrl }, { merge: true }).then(
          () => {
            postUpdateCDNUtil(currentGroup);
          },
        );
      });
    }

    if (current === "basicsStep" && !stripeSetupConfirmation) {
      setLoading(false);
      setIsTransitioning(false);
      setErrorInForm({
        isError: true,
        visible: true,
        message: "Please confirm Stripe Settings have been updated",
      });
      window.scrollTo(0, 0);
      setTimeout(handleErrorMessageClose, 5000);
      return;
    }

    if (promise) {
      promise.then((res) => {
        let data = new RegFormDetails();
        data.toDataSet(
          { ...collectFormState(), packages: currentPackages },
          current,
        );

        data = JSON.parse(JSON.stringify(data));
        if (current === "basicsStep") {
          data.groupImageFile = res;
        }

        if (data.hasError) {
          window.scrollTo(0, 0);
          setErrorInForm({
            isError: true,
            visible: true,
            message: data.hasError.message,
          });
          setLoading(false);
          setIsTransitioning(false);
          window.scrollTo(0, 0);
          setTimeout(handleErrorMessageClose, 5000);
        } else {
          delete data.packages;
          if (current === "basicsStep") {
            let requireMembershipPackageVal = data.requireMembershipPackage;
            let groupData = {
              requireMembershipPackage: requireMembershipPackageVal,
            };
            let packageIdExpiryMap = {};
            let packageExpiryMap = {};
            for (let pack of currentPackages) {
              packageIdExpiryMap[pack.id] = pack.packageExpiryDate;
              packageExpiryMap[pack.id] = {
                expiryDate: pack.packageExpiryDate || 0,
                name: pack.name || "",
              };
            }
            groupData = { ...groupData, packageIdExpiryMap, packageExpiryMap };
            fbUpdateGroup({ ...groupData }, currentGroup, null);
          }
          if (current === "membershipPackagesStep") {
            applyRegFormData(data);
            setStepState(current, "finish");
            setStepState(next, "process");
            setLoading(false);
            setIsTransitioning(false);
          } else {
            data.groupName = groupName;
            fbGetGroup(currentGroup).then((docData) => {
              data.configuration = docData.configuration || {};
              data.paymentDetails = docData.paymentDetails || {};

              fbSaveFormLayout(data, currentGroup).then(() => {
                if (current === "donationStep") {
                  handleDonationAutoRenewalSubmit(
                    docData.paymentDetails || {},
                  );
                }
                setStepState(current, "finish");
                setStepState(next, "process");
                setLoading(false);
                setIsTransitioning(false);
              });
            });
          }
        }
      });
    } else {
      let data = new RegFormDetails();
      data.toDataSet(
        { ...collectFormState(), packages: currentPackages },
        current,
      );

      data = JSON.parse(JSON.stringify(data, current));

      let hasError = false;
      if (hasError) {
        data.hasError = { message: "Image is required" };
      }

      if (data.hasError) {
        window.scrollTo(0, 0);
        setErrorInForm({
          isError: true,
          visible: true,
          message: data.hasError.message,
        });
        setLoading(false);
        setIsTransitioning(false);
        window.scrollTo(0, 0);
        setTimeout(handleErrorMessageClose, 5000);
      } else {
        delete data.packages;
        if (current === "basicsStep") {
          let requireMembershipPackageVal = data.requireMembershipPackage;
          let groupData = {
            requireMembershipPackage: requireMembershipPackageVal,
          };
          let packageIdExpiryMap = {};
          let packageExpiryMap = {};
          for (let pack of currentPackages) {
            packageIdExpiryMap[pack.id] = pack.packageExpiryDate;
            packageExpiryMap[pack.id] = {
              expiryDate: pack.packageExpiryDate || 0,
              name: pack.name || "",
            };
          }
          groupData = { ...groupData, packageIdExpiryMap, packageExpiryMap };

          if (addressVerification) {
            groupData = {
              ...groupData,
              addressVerification: addressVerification,
            };
          }
          fbUpdateGroup({ ...groupData }, currentGroup, null);
        }
        if (current === "membershipPackagesStep") {
          fbGetFormLayout(currentGroup).then((res) => {
            let data = new RegFormDetails();
            data.fromDataSet(res);
            let benefitsHtml = htmlToDraft(data.benefits);
            if (benefitsHtml) {
              const contentState = ContentState.createFromBlockArray(
                benefitsHtml.contentBlocks,
              );
              const editorState = EditorState.createWithContent(contentState);
              data.benefits = editorState;
            }
            applyRegFormData(data);
            setStepState(current, "finish");
            setStepState(next, "process");
            setLoading(false);
            setIsTransitioning(false);
          });
        } else {
          data.groupName = groupName;
          fbGetGroup(currentGroup).then((docData) => {
            data.configuration = docData.configuration || {};
            data.paymentDetails = docData.paymentDetails || {};

            fbSaveFormLayout(data, currentGroup).then(() => {
              if (current === "donationStep") {
                handleDonationAutoRenewalSubmit(
                  docData.paymentDetails || {},
                );
              }
              setStepState(current, "finish");
              setStepState(next, "process");
              setLoading(false);
              setIsTransitioning(false);
            });
          });
        }
      }
    }

    postUpdateCDNUtil(currentGroup, "join");
  };

  const handlePackageInputChange = (name, value, index) => {
    let pkgs = packages;
    if (name.includes("price")) {
      name = name.split(".")[1];
      pkgs[index]["price"][name] = value;
    } else {
      pkgs[index][name] = value;
    }
    setPackages([...pkgs]);
  };

  const handlePackageImageUploadAction = (file, index) => {
    let pkgs = packages;
    pkgs[index].image = file;
    setPackages([...pkgs]);
  };

  const handlePackageImageChange = (fileList, index) => {
    let result = {};
    if (fileList.length === 0) {
      result.image = null;
    } else {
      result.image = fileList[0].originFileObj;
    }

    result.imageList = fileList;

    let pkgs = packages;
    pkgs[index] = {
      ...pkgs[index],
      ...result,
      editConfiguration: {
        imageChanged: true,
      },
    };
    setPackages([...pkgs]);
  };

  const handlePackageCheckChange = (name, checked, index) => {
    let pkgs = packages;
    pkgs[index][name] = checked;
    setPackages([...pkgs]);
  };

  const publishOrUnpublishFormHandler = () => {
    setIsPublishing(true);
    publishOrUnpublishForm(!isPublished, currentGroup, groupName).then(
      (isPublishedResult) => {
        setIsPublishing(false);
        setIsPublished(isPublishedResult);
      },
    );
  };

  const handleUpdateTshirtSizes = (index, sizes) => {
    let pkgs = packages;
    pkgs[index].sizes = sizes;
    setPackages([...pkgs]);
  };

  const handleAddRowForSize = (index) => {
    let pkgs = packages;
    if (!pkgs[index].sizes) {
      pkgs[index].sizes = [];
    }
    pkgs[index].sizes.push("");
    setPackages([...pkgs]);
  };

  const handlePackageSelectChange = (name, value, index) => {
    let pkgs = packages;
    pkgs[index][name] = value;
    setPackages([...pkgs]);
  };

  const handleCustomFieldChange = (updatedCustomFieldDetails) => {
    setCustomFieldDetails(updatedCustomFieldDetails);
  };

  const handleFormFieldSelectChange = (fieldName, value) => {
    setFormFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handlePartnerChange = (value) => {
    if (value) {
      let partner = allPartners.find((p) => p.id === value);
      setHasDonations(true);
      setDonationTitle(partner.name);
      setDonationDesc(
        ContentState.createFromBlockArray(
          htmlToDraft(partner.details || "<p></p>"),
        ).getPlainText(""),
      );
      setDonationImageList(
        Array.from(
          { length: partner.galleryImageCount || 0 },
          (_, index) => {
            return `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${currentGroup}%2Fpartner%2F${partner.id}%2Fgallery%2F${index}?alt=media`;
          },
        ),
      );
      setDonationImage(
        partner.showLogo
          ? `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${currentGroup}%2Fpartner%2F${partner.id}%2Flogo?alt=media`
          : "",
      );
      setAmount1(partner.suggestedAmounts[0]);
      setAmount2(partner.suggestedAmounts[1]);
      setAmount3(partner.suggestedAmounts[2]);
      setPartnerId(partner.id);
    } else {
      setHasDonations(false);
      setDonationTitle("");
      setDonationDesc("<p></p>");
      setDonationImageList([]);
      setDonationImage("");
      setAmount1("");
      setAmount2("");
      setAmount3("");
      setPartnerId("");
    }
  };

  // --- Effects ---

  // componentDidMount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const scrollToPackageRequirement = urlParams.get(
      "scrollToPackageRequirement",
    );

    if (scrollToPackageRequirement === "true") {
      setTimeout(() => {
        if (requireMembershipPackageRef.current) {
          requireMembershipPackageRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setTimeout(() => {
            setHighlightPackageRequirement(true);
            setTimeout(() => {
              setHighlightPackageRequirement(false);
            }, 2500);
          }, 800);
        }
      }, 1000);
    }

    let registrationLink = `https://group.chant.fan/${groupName
      .replace(/ /g, "")
      .replace("#", "%23")
      .toLowerCase()}/join`;
    generateQR(registrationLink, null, 75).then((svg) => {
      setJoinSVG(svg);
    });

    fbGetFormLayout(currentGroup).then(async (res) => {
      if (res) {
        getWebsiteConfig(currentGroup).then((websiteData) => {
          let data = new RegFormDetails();
          data.fromDataSet(res);

          let benefitsHtml = htmlToDraft(data.benefits);
          if (benefitsHtml) {
            const contentState = ContentState.createFromBlockArray(
              benefitsHtml.contentBlocks,
            );
            const editorState = EditorState.createWithContent(contentState);
            data.benefits = editorState;
          }

          applyRegFormData(data);
          setAddressVerification(websiteData.addressVerification);
          setAppScreenshotList([
            { uid: `-1`, url: websiteData?.appScreenshot },
          ]);
          setLoading(false);

          if (
            scrollToPackageRequirement === "true" &&
            requireMembershipPackageRef.current
          ) {
            setTimeout(() => {
              requireMembershipPackageRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              setTimeout(() => {
                setHighlightPackageRequirement(true);
                setTimeout(() => {
                  setHighlightPackageRequirement(false);
                }, 2500);
              }, 800);
            }, 500);
          }
        });
      } else {
        setLoading(false);
      }
    });

    fbGetGroup(currentGroup).then((docData) => {
      if (docData.addressVerification) {
        setAddressVerification(docData.addressVerification);
      }
    });

    fbGetPartners(currentGroup).then((partners) => {
      let filteredPartners = partners.filter(
        (partner) => partner.status === "Active" && partner.collectDonations,
      );
      setAllPartners(filteredPartners);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // componentDidUpdate: currentGroup changes
  const isFirstCurrentGroupRunRef = useRef(true);
  useEffect(() => {
    if (isFirstCurrentGroupRunRef.current) {
      isFirstCurrentGroupRunRef.current = false;
      return;
    }
    setLoading(true);
    setBasicsStep("process");
    setFormFieldStep("wait");
    setPreviewStep("wait");
    setDonationStep("wait");
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // componentDidUpdate: loading && !isTransitioning triggers data fetch
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (loading && !isTransitioning) {
      fbGetFormLayout(currentGroup).then((res) => {
        if (res) {
          getWebsiteConfig(currentGroup).then((websiteData) => {
            let data = new RegFormDetails();
            data.fromDataSet(res);

            let benefitsHtml = htmlToDraft(data.benefits);
            if (benefitsHtml) {
              const contentState = ContentState.createFromBlockArray(
                benefitsHtml.contentBlocks,
              );
              const editorState = EditorState.createWithContent(contentState);
              data.benefits = editorState;
            }
            applyRegFormData(data);
            setAppScreenshotList([
              { uid: `-1`, url: websiteData?.appScreenshot },
            ]);
            setLoading(false);
          });
        } else {
          setLoading(false);
        }
      });
      fbGetPartners(currentGroup).then((partners) => {
        let filteredPartners = partners.filter(
          (partner) => partner.status === "Active" && partner.collectDonations,
        );
        setAllPartners(filteredPartners);
      });
      fbGetGroup(currentGroup).then((docData) => {
        if (docData.addressVerification) {
          setAddressVerification(docData.addressVerification);
        }
      });
    }
  }, [loading, isTransitioning]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Computed values ---

  const groupImageList = groupImageWideList?.filter((item) => {
    return (
      (item?.url && item?.url?.length > 0) ||
      (item?.uid && item?.uid?.length > 0 && item?.originFileObj)
    );
  });

  const appScreenshotListFiltered = (appScreenshotList || []).filter((item) => {
    return (
      item?.url?.length > 0 ||
      (item?.uid?.length > 0 && item?.originFileObj != null)
    );
  });

  let backgroundColor = "";
  if (
    adminData.data.configuration &&
    adminData.data.configuration.primaryColor
  ) {
    let { r, g, b } = adminData.data.configuration.primaryColor;
    backgroundColor = `rgb(${r}, ${g}, ${b})`;
  }

  const showAddressVerification = packages.some((pkg) => {
    return (
      pkg.packageExpiryDate > dayjs().unix() &&
      pkg.status === "active" &&
      (pkg.offerShipping === "shipping" || pkg.offerShipping === "both") &&
      pkg.offerAutorenewal
    );
  });

  return {
    // Refs
    requireMembershipPackageRef,

    // Group data
    currentGroup,
    adminData,
    groupName,

    // Loading/UI state
    loading,
    isTransitioning,
    isPublishing,
    isPublished,
    errorInForm,
    backgroundColor,
    highlightPackageRequirement,

    // Step state
    basicsStep,
    formFieldStep,
    donationStep,
    previewStep,
    stripeSetupConfirmation,

    // General setup fields
    title,
    intro,
    benefits,
    groupImageFile,
    appScreenshotFile,
    groupImageList,
    appScreenshotListFiltered,
    groupTermsURL,
    thankYouNote,
    supportEmail,
    processingFee,
    requireMembershipPackage,
    editConfiguration,
    joinSVG,
    showAddressVerification,
    addressVerification,
    addressVerificationTriggered,

    // Form fields
    formFields,
    customFieldDetails,

    // Chapters
    showChapterModal,
    chapters,
    includeNone,

    // Packages
    packages,

    // Donations
    hasDonations,
    donationTitle,
    donationDesc,
    donationImageList,
    donationImage,
    amount1,
    amount2,
    amount3,
    partnerId,
    allPartners,
    donationAutorenewal,

    // Handlers
    handleNext,
    handleInputChange,
    handleEditorStateChange,
    handleImageChange,
    handleStripeSetupConfirmation,
    handleCheckChange,
    handleErrorMessageClose,
    handleCustomFieldChange,
    handleFormFieldSelectChange,
    handlePartnerChange,
    changeSelect,
    uploadAction,
    handleDateChange,
    publishOrUnpublishFormHandler,

    // Package handlers
    handlePackageInputChange,
    handlePackageImageUploadAction,
    handlePackageImageChange,
    handlePackageCheckChange,
    handleUpdateTshirtSizes,
    handleAddRowForSize,
    handlePackageSelectChange,

    // Setters (for direct state updates from sub-components)
    setShowChapterModal,
    setChapters,
    setIncludeNone,
    setRequireMembershipPackage,
    setDonationAutorenewal,
    setAddressVerification,
    setAddressVerificationTriggered,
    setHighlightPackageRequirement,
  };
};

export default useRegistrationForm;
